import { useState } from "react";
import { Info } from "lucide-react";

/* ════════════════════════════════════════════════════════════════
   DEMO DATA BADGE (P0 Feb-2026)
   Subtle inline badge rendered next to the dashboard headline when
   the backend returns ``demo_mode: true``. Hover/focus shows the
   honest tooltip about why illustrative data is displayed.

   Constraints (locked by directive):
   - Do not make the dashboard look fake.
   - Do not over-warn the user.
   - Just be honest.
   ════════════════════════════════════════════════════════════════ */
export default function DemoDataBadge({
  demoMode,
  demoLabel = "Demo Data",
  dataSource,
}) {
  const [open, setOpen] = useState(false);
  if (!demoMode) return null;

  return (
    <span
      className="relative inline-flex items-center"
      data-testid="demo-data-badge"
      data-data-source={dataSource || "demo_snapshot"}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <span
        className="inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/[0.08] text-amber-300 font-medium uppercase tracking-wider cursor-help"
        tabIndex={0}
        data-testid="demo-data-badge-pill"
      >
        <Info className="w-2.5 h-2.5" />
        {demoLabel}
      </span>
      {open && (
        <div
          className="absolute top-full mt-1.5 left-0 z-50 w-64 rounded-md border border-amber-500/30 bg-slate-950/95 backdrop-blur-sm p-2.5 shadow-xl"
          role="tooltip"
          data-testid="demo-data-badge-tooltip"
        >
          <p className="text-[10px] text-slate-300 leading-relaxed">
            This account is showing controlled illustrative data until real
            financial records are committed. Once you upload bank statements
            or commit ledger entries, this badge disappears and the dashboard
            switches to your true financial picture.
          </p>
        </div>
      )}
    </span>
  );
}
